import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../AuthContext";
import SecureVideoPlayer from "../components/SecureVideoPlayer";
import { QuizTaker } from "../components/Quiz";
import { Clock, PlayCircle, Calendar, Award } from "lucide-react";
import { StatusBadge } from "./Dashboard";
import { toast } from "sonner";

export default function CourseDetail() {
  const { id } = useParams();
  const { user, API } = useAuth();
  const [data, setData] = useState(null);
  const [activeVideo, setActiveVideo] = useState(null);
  const navigate = useNavigate();

  const load = async () => {
    const r = await axios.get(`${API}/courses/${id}`, { withCredentials: true });
    setData(r.data);
    if (r.data.videos?.length && r.data.enrolled) setActiveVideo(r.data.videos[0]);
  };
  useEffect(() => { load(); }, [id]);

  if (!data) return <div className="p-10">Loading…</div>;
  const { course: c, videos, webinars, enrolled, enrollment, has_certificate } = data;

  const enroll = async () => {
    await axios.post(`${API}/courses/${id}/enroll`, {}, { withCredentials: true });
    toast.success("You're enrolled!");
    load();
  };

  const submitForReview = async () => {
    await axios.post(`${API}/courses/${id}/submit`, {}, { withCredentials: true });
    toast.success("Submitted for IT Admin approval");
    load();
  };

  const isOwner = user?.role === "teacher" && c.instructor_id === user.user_id;

  return (
    <div className="grid md:grid-cols-12 gap-8">
      <section className="md:col-span-8 space-y-6">
        <div className="flex items-center gap-3">
          <div className="overline">{c.category}</div>
          <StatusBadge s={c.status} />
        </div>
        <h1 className="font-heading text-3xl md:text-4xl font-semibold text-[#0A231C]" data-testid="course-title">{c.title}</h1>
        <p className="text-[#5C5C5C] leading-relaxed">{c.description}</p>
        <div className="flex items-center gap-5 text-sm text-[#5C5C5C]">
          <span>by <b className="text-[#1C1C1C]">{c.instructor_name}</b></span>
          <span className="flex items-center gap-1"><Clock size={14} /> {c.duration_minutes} min</span>
          <span>{c.price > 0 ? `₹${c.price}` : "Free"}</span>
        </div>

        {activeVideo ? (
          <>
            <SecureVideoPlayer videoId={activeVideo.id} courseId={c.id} onEnded={load} />
            {enrolled && activeVideo.quiz_id && (
              <QuizTaker videoId={activeVideo.id} courseId={c.id} onPassed={load} />
            )}
          </>
        ) : (
          <img src={c.cover_image} alt="" className="w-full rounded-2xl object-cover h-72" />
        )}

        <div className="card-surface p-6">
          <div className="overline mb-3">Lessons</div>
          {videos.length === 0 ? (
            <div className="text-sm text-[#5C5C5C]">No lessons uploaded yet.</div>
          ) : (
            <ul className="divide-y divide-[#F0EDE3]">
              {videos.map((v, i) => (
                <li key={v.id} className="py-3 flex items-center justify-between">
                  <button
                    onClick={() => enrolled || isOwner || user?.role === "admin" ? setActiveVideo(v) : toast.error("Enroll to watch")}
                    className="flex items-center gap-3 text-left"
                    data-testid={`play-video-${v.id}`}
                  >
                    <PlayCircle size={18} className="text-[#0A231C]" />
                    <div>
                      <div className="font-heading font-medium">{i + 1}. {v.title}</div>
                      <div className="text-xs text-[#5C5C5C]">Lesson</div>
                    </div>
                  </button>
                  {user?.role === "admin" && (
                    <a
                      href={`${API}/videos/${v.id}/download`}
                      className="text-xs text-[#D45D3C] hover:underline"
                      data-testid={`admin-download-${v.id}`}
                    >Download source</a>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="card-surface p-6">
          <div className="overline mb-3">Live webinars</div>
          {webinars.length === 0 ? (
            <div className="text-sm text-[#5C5C5C]">No webinars scheduled yet.</div>
          ) : (
            <ul className="divide-y divide-[#F0EDE3]">
              {webinars.map((w) => (
                <li key={w.id} className="py-3 flex items-center justify-between">
                  <div>
                    <div className="font-heading font-medium">{w.title}</div>
                    <div className="text-xs text-[#5C5C5C] flex items-center gap-2">
                      <Calendar size={12} /> {new Date(w.scheduled_at).toLocaleString()}
                    </div>
                  </div>
                  <a
                    href={w.meeting_link}
                    target="_blank"
                    rel="noreferrer"
                    className="btn-outline text-sm"
                    data-testid={`join-webinar-${w.id}`}
                  >Join</a>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      <aside className="md:col-span-4 space-y-4">
        <div className="card-surface p-6 sticky top-24" data-testid="enroll-panel">
          <img src={c.cover_image} alt="" className="w-full rounded-lg object-cover h-40" />
          <div className="mt-4 font-heading text-3xl font-semibold text-[#0A231C]">
            {c.price > 0 ? `₹${c.price}` : "Free"}
          </div>
          {user?.role === "learner" && (
            enrolled ? (
              <>
                <div className="mt-3 text-sm text-[#2E7D32] font-medium" data-testid="enrolled-status">✓ You're enrolled</div>
                {enrollment && (
                  <div className="mt-4">
                    <div className="overline mb-1">Progress</div>
                    <div className="h-2 rounded-full bg-[#E5E3D8] overflow-hidden">
                      <div className="h-full bg-[#0A231C]" style={{ width: `${enrollment.progress || 0}%` }} />
                    </div>
                    <div className="mt-1 text-xs text-[#5C5C5C]">{enrollment.progress || 0}% complete</div>
                  </div>
                )}
                {has_certificate && (
                  <a
                    href={`${API}/certificates/${c.id}`}
                    className="btn-primary w-full mt-4 flex items-center justify-center gap-2"
                    data-testid="download-certificate-btn"
                  >
                    <Award size={16} /> Download certificate
                  </a>
                )}
              </>
            ) : (
              c.status === "approved" && (
                <button onClick={enroll} className="btn-primary w-full mt-4" data-testid="enroll-btn">Enroll now</button>
              )
            )
          )}
          {isOwner && (
            <div className="mt-4 space-y-2">
              <button onClick={() => navigate(`/teacher/courses/${c.id}/edit`)} className="btn-outline w-full text-sm" data-testid="edit-course-btn">Edit course</button>
              {c.status === "draft" && (
                <button onClick={submitForReview} className="btn-primary w-full text-sm" data-testid="submit-for-review-btn">Submit for approval</button>
              )}
            </div>
          )}
          {user?.role === "admin" && (
            <button onClick={() => navigate("/admin/courses")} className="btn-outline w-full mt-4 text-sm">Manage in Admin</button>
          )}
        </div>
      </aside>
    </div>
  );
}
